"use client";

import { useState, useCallback, Fragment, type ReactNode, type CSSProperties } from "react";

interface EphemeralTextProps {
  children: string;
  /** How long letters stay affected (ms). Default: 1000 */
  restoreDuration?: number;
  /** Opacity when hovered. 0 = disappear, 0.6 = dim. Default: 0 */
  hoverOpacity?: number;
  /** Additional classes for the wrapper span */
  className?: string;
  /** Style for the wrapper span */
  style?: CSSProperties;
  /** Render as different element. Default: span */
  as?: "span" | "p" | "h1" | "h2" | "h3" | "div";
  /** Click handler */
  onClick?: () => void;
}

/**
 * Ephemeral Text - letters fade on hover, then restore.
 * A playful easter egg interaction that rewards cursor exploration.
 *
 * Usage:
 * <EphemeralText>Vincent Ngu</EphemeralText>           // disappear (default)
 * <EphemeralText hoverOpacity={0.4}>Dim me</EphemeralText>  // dim to 40%
 */
export function EphemeralText({
  children,
  restoreDuration = 1000,
  hoverOpacity = 0,
  className = "",
  style,
  as: Component = "span",
  onClick,
}: EphemeralTextProps) {
  const [hiddenIndices, setHiddenIndices] = useState<Set<number>>(new Set());

  const handleLetterHover = useCallback((index: number) => {
    setHiddenIndices((prev) => {
      // Skip if already hidden
      if (prev.has(index)) return prev;

      const next = new Set(prev);
      next.add(index);

      // Schedule restoration
      setTimeout(() => {
        setHiddenIndices((current) => {
          const updated = new Set(current);
          updated.delete(index);
          return updated;
        });
      }, restoreDuration);

      return next;
    });
  }, [restoreDuration]);

  return (
    <Component className={className} style={style} onClick={onClick}>
      {children.split("").map((letter, i) => {
        // Render newlines as line breaks
        if (letter === "\n") {
          return <br key={i} />;
        }
        return (
          <span
            key={i}
            onMouseEnter={() => letter !== " " && handleLetterHover(i)}
            className={letter !== " " ? "pointer-events-auto" : ""}
            style={{
              opacity: hiddenIndices.has(i) ? hoverOpacity : 1,
              transition: "opacity var(--duration-slow) var(--ease-out)",
            }}
          >
            {letter}
          </span>
        );
      })}
    </Component>
  );
}
